import { AuthErrorField, AuthErrorKind } from '@griply/types';
import { getAuthErrorField, getAuthErrorMessageKey } from '@griply/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import {
  FormInput,
  Sizes as InputSizes,
} from 'shared/components/ui/form-input';
import { Paths } from 'shared/routes/paths';
import { LoginFormFields } from 'shared/types/login-form';

import * as Styled from './login-form.style';
import { useLoginForm } from './use-login-form';

export type LoginFormProps = {
  onSubmit: (loginData: LoginFormFields) => void;
  isLoading: boolean;
  error?: AuthErrorKind;
};

export const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  const { register, onSubmit: submitForm, errors } = useLoginForm(onSubmit);

  const errorMessageKey = error ? getAuthErrorMessageKey(error) : null;
  const errorMessage = errorMessageKey ? t(errorMessageKey) : undefined;
  const errorField = error ? getAuthErrorField(error) : null;

  const emailErrorMessage = useMemo(() => {
    if (errors.email?.message) {
      return t('forms.login.fields.email.error');
    }

    if (errorField === AuthErrorField.Email) {
      return errorMessage;
    }
  }, [errorField, errorMessage, errors.email?.message, t]);

  const passwordErrorMessage = useMemo(() => {
    if (errors.password?.message) {
      return t('forms.login.fields.password.error');
    }

    if (errorField === AuthErrorField.Password) {
      return errorMessage;
    }

    return undefined;
  }, [errorField, errorMessage, errors.password?.message, t]);

  const formErrorMessage =
    errorField === AuthErrorField.Form ? errorMessage : undefined;

  return (
    <Styled.Form noValidate onSubmit={submitForm}>
      <Styled.Fields>
        <Styled.FieldContainer>
          <FormInput
            {...register('email')}
            id="email"
            type="email"
            autoComplete="email"
            placeholder={t('forms.login.fields.email.placeholder')}
            error={emailErrorMessage}
            size={InputSizes.Large}
            disabled={isLoading}
          />
          {!!emailErrorMessage && (
            <Styled.ErrorMessage>{emailErrorMessage}</Styled.ErrorMessage>
          )}
        </Styled.FieldContainer>

        <Styled.FieldContainer>
          <FormInput
            {...register('password')}
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder={t('forms.login.fields.password.placeholder')}
            error={passwordErrorMessage}
            size={InputSizes.Large}
            disabled={isLoading}
          />
          {!!passwordErrorMessage && (
            <Styled.ErrorMessage>{passwordErrorMessage}</Styled.ErrorMessage>
          )}
        </Styled.FieldContainer>
      </Styled.Fields>

      {!!formErrorMessage && (
        <Styled.ErrorMessage role="alert">
          {formErrorMessage}
        </Styled.ErrorMessage>
      )}

      <Styled.Interactions>
        <Styled.Anchor to={Paths.ForgotPassword}>
          {t('forms.login.buttons.forgot-password.label')}
        </Styled.Anchor>

        <Button
          variant={Variants.Contained}
          color={Colors.Primary}
          size={Sizes.Large}
          type="submit"
          disabled={isLoading}
          fullWidth
          alignCenter
          disabledBackgroundOnly
        >
          {isLoading ? (
            <Styled.Loader color="primary" size={20} />
          ) : (
            t('forms.login.buttons.sign-in.label')
          )}
        </Button>
      </Styled.Interactions>
    </Styled.Form>
  );
};
